import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router";

import type { ChartOptions, TooltipItem } from "chart.js";
import {
  CopyIcon,
  DownloadIcon,
  InfoIcon,
  QrCodeIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import QRCodeStyling from "qr-code-styling";

import { formatDateRelative } from "simplydo/core/dates";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H2 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import { TableCell, TableRow } from "simplydo/react/ui/table";

import AppIcon from "src/images/appIcon.png";
import { useAppSelector } from "store";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import type {
  LineChartData,
  LineChartOptions,
  LinkChartDataFn,
  ShortenedLink,
} from "../types";

const chartOptions: LineChartOptions = {
  layout: { padding: { right: 8, top: 4, left: 3, bottom: 4 } },
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      ticks: { display: false },
      grid: { color: "rgba(0,0,0,0.03)", drawBorder: false },
    },

    x: {
      type: "time",
      grid: { color: "rgba(0,0,0,0)", display: true },
      display: false,
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        title() {},
        label(tooltipItem: TooltipItem<"line">) {
          const value = tooltipItem.formattedValue;
          return `${value} clicks`;
        },
      },
    },
  },
} as ChartOptions<"line">;

type LinkRowProps = {
  link: ShortenedLink;
  deleteUrl: (id: string) => void;
  copyLink: (link: string) => void;
  chartData: LinkChartDataFn;
  disableReferrals?: boolean;
};

const LinkRow = ({
  link,
  deleteUrl,
  copyLink,
  chartData: getChartData,
  disableReferrals = false,
}: LinkRowProps) => {
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const navigate = useNavigate();

  const chartData = useMemo(
    (): LineChartData | null => getChartData(link),
    [link, getChartData],
  );
  const theme = useTheme();
  const qrRef = useRef<HTMLDivElement>(null);

  const user = useAppSelector((state) => state.user);
  const canChangeSettings = util.hasPermission(
    user,
    "org.changeSettings",
    user?.ownerOrganisation?._id,
  );

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: `${sdiClickDomain}/${link.path}`,
        image: AppIcon,
        dotsOptions: { color: "#14435B" },
        backgroundOptions: { color: "#ffffff" },
        imageOptions: { margin: 4 },
      }),
    [link],
  );

  useEffect(() => {
    if (qrCodeModalOpen) {
      qrCode.append(qrRef.current as HTMLElement);
    }
  }, [qrCodeModalOpen, qrCode]);

  const downloadQrCode = useCallback(
    (extension: "png" | "jpeg" | "svg") => {
      qrCode.download({ name: `simpylydo-${link.path}`, extension });
    },
    [qrCode, link],
  );

  return (
    <>
      <Modal
        style={{ marginTop: 50 }}
        open={qrCodeModalOpen}
        onClose={() => setQrCodeModalOpen(false)}
      >
        <ModalHeader>
          <H2>Link QR code</H2>
        </ModalHeader>
        <ModalContent>
          <p>
            Download this QR code and share it with users to take them directly
            to the link. Scanning the QR code using a camera will take you
            directly to the link.
          </p>
          <div
            style={{ width: "70%", margin: "10px auto", textAlign: "center" }}
            ref={qrRef}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setQrCodeModalOpen(false)}>
            <Icon icon={XIcon} />
            Cancel
          </Button>
          <Dropdown
            trigger={
              <Button>
                <Icon icon={DownloadIcon} />
                Download QR code
              </Button>
            }
          >
            <DropdownMenu>
              <DropdownItem onClick={() => downloadQrCode("png")}>
                ... download as PNG
              </DropdownItem>
              <DropdownItem onClick={() => downloadQrCode("jpeg")}>
                ... download as JPEG
              </DropdownItem>
              <DropdownItem onClick={() => downloadQrCode("svg")}>
                ... download as SVG
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </ModalActions>
      </Modal>
      <TableRow key={link._id}>
        <TableCell>
          <a
            href={`${sdiClickDomain}/${link.path}`}
            target="_blank"
            rel="noopener noreferrer"
          >{`${sdiClickDomain}/${link.path}`}</a>
          <span
            style={{ marginLeft: 5, cursor: "pointer" }}
            data-tooltip="Copy to clipboard"
            onClick={() => copyLink(link.path)}
          >
            <Icon icon={CopyIcon} />
          </span>
          <br />
          <small style={{ overflowWrap: "anywhere" }}>
            Resolves to:{" "}
            <a href={link.url} target="_blank" rel="noopener noreferrer">
              {link.url}
            </a>
          </small>
          <br />{" "}
          <small style={{ overflowWrap: "anywhere" }}>
            {link?.description}
          </small>
        </TableCell>
        {theme.sizes.isMobile ? (
          <>
            <TableCell>
              Created {formatDateRelative(link.createdAt, { addSuffix: true })}
              {link.clicks || 0} clicks
            </TableCell>
            <TableCell />
            <TableCell />
          </>
        ) : (
          <>
            <TableCell>
              {formatDateRelative(link.createdAt, { addSuffix: true })}
            </TableCell>
            <TableCell>{link.clicks || 0}</TableCell>
            {chartData ? (
              <TableCell>
                <div style={{ width: 400, height: 75 }}>
                  <Line
                    height={75}
                    width={400}
                    options={chartOptions}
                    data={chartData}
                  />
                </div>
              </TableCell>
            ) : null}
          </>
        )}
        <TableCell collapsing textAlign="right">
          {!disableReferrals ? (
            <Popup
              on="hover"
              trigger={
                <Button
                  size="small"
                  onClick={() => navigate(`/admin/links/${link._id}`)}
                >
                  <Icon icon={InfoIcon} />
                </Button>
              }
            >
              Show user capture information from this link
            </Popup>
          ) : null}

          <Popup
            on="hover"
            trigger={
              <Button size="small" onClick={() => setQrCodeModalOpen(true)}>
                <Icon icon={QrCodeIcon} />
              </Button>
            }
          >
            Download a QR code to share this link with users.{" "}
          </Popup>
          {canChangeSettings ? (
            <Button
              size="small"
              onClick={() => deleteUrl(link._id)}
              variant="outline"
            >
              <Icon icon={Trash2Icon} />
            </Button>
          ) : null}
        </TableCell>
      </TableRow>
    </>
  );
};

export default LinkRow;
